/**
 * 汇率数据管理Hook
 * 职责: 管理汇率数据加载和更新
 * 限制: <100行
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import type { Dayjs } from 'dayjs';
import { exchangeRateAPI, type GetTrendsParams } from '../../../services/exchangeRateService';
import type { LatestRate, TrendData } from '../../../types/exchange-rate-types';

const MAIN_CURRENCIES = ['USD', 'EUR', 'JPY', 'GBP'];

export const useExchangeRateData = (
    selectedCurrencies: string[],
    timeRange: 'week' | 'month' | 'half_year' | 'year' | 'custom',
    customDateRange: [Dayjs, Dayjs]
) => {
    const [latestRates, setLatestRates] = useState<LatestRate[]>([]);
    const [trendData, setTrendData] = useState<TrendData[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const loadLatestRates = useCallback(async () => {
        try {
            const response = await exchangeRateAPI.getLatestRates({
                currencies: MAIN_CURRENCIES.join(',')
            });
            setLatestRates(response.data);
        } catch (error) {
            console.error('Load latest rates error:', error);
        }
    }, []);

    const loadTrendData = useCallback(async () => {
        if (selectedCurrencies.length === 0) return;

        setLoading(true);
        try {
            const days = calculateDays(timeRange, customDateRange);
            const params: GetTrendsParams = {
                currencies: selectedCurrencies.join(','),
                days: Math.min(days, 365)
            };

            const response = await exchangeRateAPI.getTrends(params);
            setTrendData(response.data);
        } catch (error) {
            console.error('Load trend data error:', error);
            message.error('加载趋势数据失败');
        } finally {
            setLoading(false);
        }
    }, [selectedCurrencies, timeRange, customDateRange]);

    const handleFetchRates = async () => {
        setFetching(true);
        try {
            const response = await exchangeRateAPI.fetchRates();
            message.success(response.data.message);
            await loadLatestRates();
            await loadTrendData();
        } catch (error) {
            message.error('更新失败，请稍后重试');
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            await loadLatestRates();
            try {
                await exchangeRateAPI.fetchRates();
                await loadLatestRates();
            } catch (e) {
                console.warn('Auto fetch failed:', e);
            }
            loadTrendData();
        };
        init();
    }, [loadLatestRates, loadTrendData]);

    return {
        latestRates,
        trendData,
        loading,
        fetching,
        handleFetchRates
    };
};

const calculateDays = (
    timeRange: string,
    customDateRange: [Dayjs, Dayjs]
): number => {
    if (timeRange === 'week') return 7;
    if (timeRange === 'month') return 30;
    if (timeRange === 'half_year') return 180;
    if (timeRange === 'year') return 365;
    if (timeRange === 'custom') {
        return customDateRange[1].diff(customDateRange[0], 'day');
    }
    return 30;
};
